import { isBoolean, isDate, isEmpty, get, set, last, each, extend, indexOf, cloneDeep, isObject, keys, isString, isNumber, chain } from 'lodash';
import axios from 'axios';
import debug from 'debug';
import { enhanceResponseWithEtag, formatPayload } from './helpers.js';
import * as OT from '../openapi-tools.js';
const log = debug('itp:core:connections:openapi');
const REQUEST_TIMEOUT_MS = 20000;
function isEmptyValue(value) {
    if (value == null) {
        return true;
    }
    if (isBoolean(value) || isNumber(value) || isDate(value)) {
        return false;
    }
    return isEmpty(value);
}
function getReference(doc, path) {
    if (!path.startsWith('#')) {
        throw new Error(`Invalid $ref path. It must start with "#" character. path=${path}`);
    }
    let result = get(doc, path.split('/').slice(1));
    if (!result) {
        throw new Error(`$ref not found: ${path}`);
    }
    return result;
}
function valueAs(doc, data) {
    if (get(data, '$ref', null) !== null) {
        return getReference(doc, data.$ref);
    }
    return data;
}
export function getOperationNEXT(doc, operationId) {
    if (operationId.indexOf('/') > -1) {
        const lastToken = last(operationId.split('/'));
        if (lastToken) {
            operationId = lastToken;
        }
    }
    let result = null;
    each(doc.paths, (pathItem, url) => {
        each(pathItem, (op, method) => {
            if (get(op, 'operationId') === operationId) {
                result = op;
            }
        });
    });
    if (!result) {
        throw new Error(`invalid OpenAPI operationId: ${operationId}`);
    }
    return result;
}
function getOperation(doc, id) {
    let result = null;
    if (id.indexOf('/') > -1) {
        const lastToken = last(id.split('/'));
        if (lastToken) {
            id = lastToken;
        }
    }
    const paths = get(doc, 'paths', {});
    each(paths, (v, url) => each(v, (opts, method) => {
        if (opts.operationId === id) {
            result = extend({ url, method }, opts);
            return false;
        }
    }));
    if (!result) {
        log(doc);
        throw new Error(`OpenAPI operation not found: ${id}.`);
    }
    return result;
}
export function hasProperty(name, schema) {
    return !!(schema.properties && name in schema.properties);
}
function getProperty(name, schema, doc) {
    let result;
    if (schema.properties && name in schema.properties) {
        result = valueAs(doc, schema.properties[name]);
    }
    if (!result) {
        throw new Error(`OpenAPI property not found: ${name}. (schema=${schema.title || schema.description})`);
    }
    return result;
}
export function getPropertyRules(schema, property, doc) {
    const sch = OT.getSchema(doc, schema);
    if (!hasProperty(property, sch)) {
        return { 'readonly': true };
    }
    const prop = getProperty(property, sch, doc), result = {};
    if (prop.maximum != null) {
        result['max_value'] = prop.maximum;
    }
    if (prop.minimum != null) {
        result['min_value'] = prop.minimum;
    }
    if (prop.maxLength != null) {
        result['max'] = prop.maxLength;
    }
    if (prop.minLength != null) {
        result['min'] = prop.minLength;
    }
    if (indexOf(sch.required, property) > -1) {
        result['required'] = true;
    }
    if (prop.enum) {
        result['oneOf'] = cloneDeep(prop.enum);
    }
    return result;
}
function getContentType(op) {
    if (op.requestBody) {
        const content = get(op.requestBody, 'content');
        if (isObject(content)) {
            return keys(content)[0];
        }
    }
}
;
export class OpenapiSchema {
    doc;
    constructor(schema, doc) {
        Object.assign(this, schema);
        this.doc = doc;
    }
    hasProperty(name) {
        return !!(this.properties && name in this.properties);
    }
    getProperty(name) {
        return getProperty(name, this, this.doc);
    }
}
export class Openapi {
    id;
    config;
    doc;
    acceptLanguage;
    constructor(doc, spec, language) {
        this.id = spec.name;
        this.doc = doc;
        this.config = spec;
        this.acceptLanguage = language;
    }
    static load(spec, language) {
        if (spec.schema) {
            log(`create "${spec.name}" connection from offline schema`);
            return Promise.resolve(new Openapi(spec.schema, spec, language));
        }
        log(`[${spec.name}] loading ${spec.url}`);
        return axios
            .get(spec.url, { timeout: REQUEST_TIMEOUT_MS })
            .then(resp => new Openapi(resp.data, spec, language));
    }
    getPropertyRules(schema, property) {
        return getPropertyRules(schema, property, this.doc);
    }
    getOpenapiSchema(name) {
        return new OpenapiSchema(OT.getSchema(this.doc, name), this.doc);
    }
    getSchema(name) {
        return OT.getSchema(this.doc, name);
    }
    getSchemaDefaults(name) {
        return OT.getSchemaDefaults(this.doc, OT.getSchema(this.doc, name));
    }
    getOperationResponseSchema(operationId, response = '200', content = 'application/json') {
        const op = getOperationNEXT(this.doc, operationId), resp = valueAs(this.doc, op.responses[response]);
        let schema = resp.content ? resp.content[content].schema : null;
        if (!schema) {
            throw new TypeError(`Cannot resolve for ${op.operationId}`);
        }
        return valueAs(this.doc, schema);
    }
    getOperation(name) {
        return getOperation(this.doc, name);
    }
    urlFor(op, args, content) {
        const operation = isString(op) ? this.getOperation(op) : op;
        const paramNamesUsed = [];
        let result = `${this.config.baseUrl}${operation.url}`;
        function getvalue(name, required = true) {
            let value = args ? get(args, name) : null;
            if (value == null && content) {
                value = get(content, name);
            }
            if (required && isEmptyValue(value)) {
                throw new Error(`Parameter value required for "${name}".`);
            }
            if (isString(value)) {
                return encodeURIComponent(value);
            }
            return value;
        }
        chain(operation.parameters)
            .filter({ in: 'path' })
            .each(p => {
            let value = getvalue(p.name, p.required);
            if (!isEmptyValue(value)) {
                result = result.replace(`{${p.name}}`, value);
                paramNamesUsed.push(p.name);
            }
        })
            .value();
        const matches = result.match(/\{([\w-]+)\}/g);
        if (matches) {
            each(matches, match => result = result.replace(match, getvalue(match.slice(1, -1), true)));
        }
        const queryParameters = [];
        chain(operation.parameters)
            .filter({ in: 'query' })
            .each(p => {
            let value = getvalue(p.name, p.required);
            if (value != null) {
                queryParameters.push(`${p.name}=${value}`);
                paramNamesUsed.push(p.name);
            }
        })
            .value();
        if (args) {
            for (let [name, value] of Object.entries(args)) {
                if (paramNamesUsed.indexOf(name) > -1) {
                    continue;
                }
                if (value != null) {
                    queryParameters.push(`${name}=${value}`);
                }
            }
        }
        if (queryParameters.length) {
            result += '?' + queryParameters.join('&');
        }
        log('urlFor [%o] result is %s', op, result);
        return result;
    }
    headersFor(op, args) {
        let result = extend({}, this.config.headers, args);
        if (!args) {
            return result;
        }
        extend(result, args.headers);
        const content = args.content, params = args.parameters;
        if (content && ['put', 'patch', 'delete'].indexOf(op.method) > -1) {
            const etag = get(content, '$etag') ||
                get(content, 'etag') ||
                (params && (params['$etag'] || params['etag']));
            if (etag) {
                result['If-Match'] = etag;
            }
        }
        const contentType = args.contentType || getContentType(op);
        if (contentType) {
            result['Content-Type'] = contentType;
        }
        if (args.language || this.acceptLanguage) {
            result['Accept-Language'] = `${args.language || this.acceptLanguage};q=0.9,en;q=0.5`;
        }
        return result;
    }
    request(path, args) {
        log('request');
        log('path: %o', path),
            log('args: %o', args);
        let parameters = args ? args.parameters : undefined, content = args ? args.content : undefined, op = this.getOperation(path), url = this.urlFor(op, parameters, content), headers = this.headersFor(op, args), data = content;
        log('url: %o', url);
        if (!isEmpty(headers)) {
            log('headers: %o', headers);
        }
        if (data && headers && headers['Content-Type']) {
            data = formatPayload(data, headers['Content-Type']);
            log('data: %o', data);
        }
        const extras = {};
        if (args && args.responseType) {
            extras.responseType = args.responseType;
        }
        return this.ajax({
            method: op.method,
            url,
            data,
            headers,
            timeout: REQUEST_TIMEOUT_MS,
            ...extras
        });
    }
    ajax(config) {
        const language = config.headers?.['Accept-Language'];
        if (!language && this.acceptLanguage) {
            if (!config.headers) {
                config.headers = {};
            }
            config.headers['Accept-Language'] = `${this.acceptLanguage};q=0.9,en;q=0.5`;
        }
        return axios(config)
            .then(response => {
            response = enhanceResponseWithEtag(response);
            return response;
        });
    }
    login(username, password) {
        return this
            .request('login_for_access_token_token_post', {
            content: {
                username,
                password,
                grant_type: 'password'
            }
        })
            .then(resp => {
            const access_token = resp.data.access_token;
            if (this.config.headers) {
                set(this.config.headers, 'Authorization', `Bearer ${access_token}`);
            }
            return access_token;
        });
    }
    valueOf(ref) {
        return valueAs(this.doc, ref);
    }
    valueAs(ref) {
        return valueAs(this.doc, ref);
    }
}
//# sourceMappingURL=openapi.js.map